<div [ngClass]="this.clientSetup.loginStyle || 'login-container-stream'">
  <div class="login-card">
    <div class="login-brand">
      <ng-container *ngIf="clientSetup.loginLogo; else defaultBrand">
        <img
          [src]="clientSetup.loginLogo"
          alt="The Stream"
          class="login-brand-image"
          [ngClass]="clientSetup.loginLogoStyle || ''"
        />
      </ng-container>
      <ng-template #defaultBrand>
        <img class="login-brand-mark" src="/assets/icons2/The-Stream_mark-only_transparent-for-dark.svg" alt="" />
        <span class="login-brand-name">The Stream</span>
      </ng-template>
    </div>

    <div class="login-panel">
      <form
        [formGroup]="loginForm"
        (ngSubmit)="login()"
        class="login-form"
        *ngIf="!mustChangePassword && !wantsToResetPassword && !resettingPassword"
      >
        <h1 class="login-title">{{ 'Sign in with The Stream' | translate }}</h1>

        <div class="login-fields">
          <div class="login-field">
            <label class="sr-only" for="login-username">{{ 'Email Address' | translate }}</label>
            <input
              id="login-username"
              class="login-input"
              type="email"
              autocomplete="email"
              placeholder="{{ 'Email Address' | translate }}"
              formControlName="username"
            />
            <p
              class="login-error"
              *ngIf="loginForm.controls['username'].hasError('required') && loginForm.controls['username'].touched"
            >
              {{ 'Username is required' | translate }}
            </p>
          </div>

          <div class="login-field">
            <label class="sr-only" for="login-password">{{ 'Password' | translate }}</label>
            <input
              id="login-password"
              class="login-input"
              type="password"
              autocomplete="current-password"
              placeholder="{{ 'Password' | translate }}"
              formControlName="password"
            />
            <p
              class="login-error"
              *ngIf="loginForm.controls['password'].hasError('required') && loginForm.controls['password'].touched"
            >
              {{ 'Password is required' | translate }}
            </p>
          </div>
        </div>

        <div class="login-links-row">
          <a class="login-link" (click)="wantsToResetPassword = true">{{ 'Forgotten your password?' | translate }}</a>
        </div>

        <div class="login-actions">
          <button class="login-submit" type="submit" [disabled]="!loginForm.valid">
            <span>{{ 'Log In' | translate }}</span>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12h13M13 7l5 5-5 5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <div class="login-sso">
          <div class="login-divider">
            <span>{{ 'Or continue with' | translate }}</span>
          </div>
          <div class="login-sso-buttons">
            <button
              type="button"
              class="login-sso-button"
              aria-label="Sign in with Microsoft"
              (click)="federatedLogin('Microsoft')"
            >
              <img class="login-sso-image" src="/assets/img/microsoft-signin.png" alt="" />
            </button>
            <button
              type="button"
              class="login-sso-button"
              aria-label="Sign in with Google"
              (click)="federatedLogin('Google')"
            >
              <img class="login-sso-image" src="/assets/img/google-signin-light.png" alt="" />
            </button>
          </div>
        </div>
      </form>

      <form
        [formGroup]="startForgotPasswordForm"
        (ngSubmit)="startForgotPassword()"
        class="login-form"
        *ngIf="wantsToResetPassword"
      >
        <h1 class="login-title">{{ 'Reset your password' | translate }}</h1>

        <div class="login-fields">
          <div class="login-field">
            <label class="sr-only" for="login-reset-email">{{ 'Email Address' | translate }}</label>
            <input
              id="login-reset-email"
              class="login-input"
              type="email"
              autocomplete="email"
              placeholder="{{ 'Email Address' | translate }}"
              formControlName="email"
            />
            <p
              class="login-error"
              *ngIf="
                startForgotPasswordForm.controls['email'].hasError('required') &&
                startForgotPasswordForm.controls['email'].touched
              "
            >
              {{ 'Email is required' | translate }}
            </p>
          </div>
        </div>

        <div class="login-links-row">
          <a class="login-link" (click)="wantsToResetPassword = false">{{ 'Cancel' | translate }}</a>
        </div>

        <div class="login-actions">
          <button class="login-submit" type="submit" [disabled]="!startForgotPasswordForm.valid">
            <span>{{ 'Reset Password' | translate }}</span>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12h13M13 7l5 5-5 5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </form>

      <form
        [formGroup]="setNewPasswordForm"
        (ngSubmit)="setNewPassword()"
        class="login-form"
        *ngIf="mustChangePassword || resettingPassword"
      >
        <h1 class="login-title">
          {{ resettingPassword ? ('Reset your password' | translate) : ('Set a new password' | translate) }}
        </h1>

        <div class="login-fields">
          <div class="login-field" *ngIf="resettingPassword">
            <label class="sr-only" for="login-reset-code">{{ 'Code' | translate }}</label>
            <input
              id="login-reset-code"
              class="login-input"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder="{{ 'Code' | translate }}"
              formControlName="code"
            />
            <p
              class="login-error"
              *ngIf="setNewPasswordForm.controls['code'].touched && !setNewPasswordForm.controls['code'].value"
            >
              {{ 'A code is required to reset your password. Please check your email for the code.' | translate }}
            </p>
            <p
              class="login-error"
              *ngIf="
                setNewPasswordForm.controls['code'].touched &&
                setNewPasswordForm.controls['code'].value &&
                !setNewPasswordForm.controls['code'].valid
              "
            >
              {{ 'Please enter the six numbers sent to your email address for the code.' | translate }}
            </p>
          </div>

          <div class="login-field">
            <label class="sr-only" for="login-new-password">{{ 'New password' | translate }}</label>
            <input
              id="login-new-password"
              class="login-input"
              type="password"
              autocomplete="new-password"
              placeholder="{{ 'New password' | translate }}"
              formControlName="newPassword"
            />
            <p
              class="login-error"
              *ngIf="
                setNewPasswordForm.controls['newPassword'].hasError('required') &&
                setNewPasswordForm.controls['newPassword'].touched
              "
            >
              {{ 'New password is required' | translate }}
            </p>
            <p
              class="login-error"
              *ngIf="
                setNewPasswordForm.controls['newPassword'].value &&
                !passwordStrength.test(setNewPasswordForm.controls['newPassword'].value) &&
                setNewPasswordForm.controls['newPassword'].touched
              "
            >
              {{
                'Password must be at least eight (8) characters long and contain numbers, uppercase and lowercase letters and at least one special character.'
                  | translate
              }}
            </p>
          </div>

          <div class="login-field">
            <label class="sr-only" for="login-repeat-password">{{ 'Repeat new password' | translate }}</label>
            <input
              id="login-repeat-password"
              class="login-input"
              type="password"
              autocomplete="new-password"
              placeholder="{{ 'Repeat new password' | translate }}"
              formControlName="repeatNewPassword"
            />
            <p
              class="login-error"
              *ngIf="
                setNewPasswordForm.controls['repeatNewPassword'].hasError('required') &&
                setNewPasswordForm.controls['repeatNewPassword'].touched
              "
            >
              {{ 'Confirm new password' | translate }}
            </p>
            <p
              class="login-error"
              *ngIf="
                setNewPasswordForm.controls['repeatNewPassword'].value !==
                  setNewPasswordForm.controls['newPassword'].value &&
                setNewPasswordForm.controls['repeatNewPassword'].touched
              "
            >
              {{ 'Passwords do not match' | translate }}
            </p>
          </div>
        </div>

        <div class="login-links-row">
          <a
            class="login-link"
            (click)="mustChangePassword = false; resettingPassword = false; wantsToResetPassword = false"
            >{{ 'Cancel' | translate }}</a
          >
        </div>

        <div class="login-actions">
          <button
            class="login-submit"
            type="submit"
            [disabled]="
              setNewPasswordForm.controls['repeatNewPassword'].value !==
                setNewPasswordForm.controls['newPassword'].value || !setNewPasswordForm.valid
            "
          >
            <span>{{ 'Set New Password' | translate }}</span>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12h13M13 7l5 5-5 5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
